import React, { useState, useEffect, useRef, useCallback } from "react";
import { motion } from "framer-motion";
import { getFirefoxCssFix } from "../commonfunctions/getFirefoxCssFix";
import DropdownFramer from "../framerMotion/DropdownFramer";
import Image from "../images/Image";
import DropDownModel from "./DropDownModel";
import ErrorMessage from "../ErrorMessages/ErrorMessage";

interface NestedDropdownItem {
  label: string;
  value: string;
  isNested?: boolean;
  apiEndpoint?: string;
  id?: string;
  testId?: string;
}

interface UserData {
  user_name: string;
  user_code: string;
  status: string;
  [key: string]: any;
}

interface NestedDropdownProps {
  options: NestedDropdownItem[];
  onSelect?: (value: any, action?: boolean) => void;
  id?: string;
  className?: string;
  style?: React.CSSProperties;
  dropdownTopClass?: string;
  testId?: string;
  selectedData?: any;
  errorMessage?: string;
  fetchUserData?: (endpoint: string, searchTerm: string, page?: number) => Promise<{
    data: { users: UserData[]; total_count: number };
  }>;
  fetchUsers?: (searchTerm: string, page?: number) => Promise<{
    data: { users: UserData[]; total_count: number };
  }>;
  showStatus?: boolean;
  placeholder?: string;
}

const NestedDropdown: React.FC<NestedDropdownProps> = ({
  options,
  onSelect,
  id,
  className,
  dropdownTopClass,
  testId,
  selectedData,
  errorMessage,
  fetchUserData = async () => ({ data: { users: [], total_count: 0 } }),
  fetchUsers,
  showStatus = true,
  placeholder = "Select parent user",
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const [selectedOption, setSelectedOption] = useState<string | null>(selectedData || null);
  const [users, setUsers] = useState<UserData[]>([]);
  const [loading, setLoading] = useState(false);
  const [internalSearchTerm, setInternalSearchTerm] = useState("");
  const [currentPage, setCurrentPage] = useState(1);
  const [hasMoreData, setHasMoreData] = useState(false);
  const buttonRef = useRef<HTMLDivElement>(null);
  const [buttonWidth, setButtonWidth] = useState(0);
  const debounceRef = useRef<ReturnType<typeof setTimeout>>();

  useEffect(() => {
    setSelectedOption(selectedData || null);
  }, [selectedData]);

  useEffect(() => {
    if (buttonRef.current) {
      setButtonWidth(buttonRef.current.offsetWidth);
    }
  }, [isOpen]);

  const nestedOptions = options.filter((o) => o.isNested && o.apiEndpoint);
  const staticOptions = options.filter((o) => !o.isNested);

  const loadUsers = useCallback(
    async (searchTerm: string, page: number = 1, append: boolean = false) => {
      setLoading(true);
      try {
        let newUsers: UserData[] = [];
        let totalCount = 0;
        if (fetchUsers) {
          const result = await fetchUsers(searchTerm, page);
          newUsers = result.data.users;
          totalCount = result.data.total_count;
        } else {
          const results = await Promise.all(
            nestedOptions.map((o) => fetchUserData(o.apiEndpoint!, searchTerm, page))
          );
          newUsers = results.flatMap((r) => r.data.users);
          totalCount = results.reduce((sum, r) => sum + r.data.total_count, 0);
        }
        setUsers(prev => append ? [...prev, ...newUsers] : newUsers);
        setCurrentPage(page);
        setHasMoreData(totalCount > (append ? users.length + newUsers.length : newUsers.length));
      } catch (error) {
        console.error("Error fetching users:", error);
        if (!append) setUsers([]);
      } finally {
        setLoading(false);
      }
    },
    [fetchUsers, fetchUserData, nestedOptions.map((o) => o.apiEndpoint).join(",")]
  );

  // Load initial users when dropdown opens
  useEffect(() => {
    if (isOpen) {
      loadUsers("", 1);
    }
  }, [isOpen]);

  const handleSearch = (value: string) => {
    setInternalSearchTerm(value);
    if (debounceRef.current) clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(() => {
      loadUsers(value.trim(), 1);
    }, 500);
  };

  const handleScroll = (event: React.UIEvent<HTMLDivElement>) => {
    const target = event.target as HTMLDivElement;
    const scrollPosition = Math.ceil(target.scrollTop + target.clientHeight);
    const threshold = target.scrollHeight - 20;
    if (scrollPosition >= threshold && hasMoreData && !loading) {
      loadUsers(internalSearchTerm.trim(), currentPage + 1, true);
    }
  };

  const handleClose = (action: boolean) => {
    setIsOpen(action);
    if (!action) {
      setInternalSearchTerm("");
      setUsers([]);
    }
  };

  const handleSelect = (user: UserData) => {
    onSelect && onSelect(user, false);
    setIsOpen(false);
    setInternalSearchTerm("");
    setUsers([]);
  };

  return (
    <DropDownModel isOpen={isOpen} handleClose={handleClose} modalStyle={className}>
      <div className="relative" data-testid={testId}>
        {/* Trigger button */}
        <div
          ref={buttonRef}
          className={`flex items-center justify-between py-3 w-full ${
            selectedOption ? "text-black" : "text-gray-400"
          } h-14 text-subTitle ${isOpen ? "border-2 px-[15px]" : "border px-4"} ${
            errorMessage ? "border-red-500" : "border-P_light_active"
          } rounded-lg cursor-pointer`}
          onClick={() => handleClose(!isOpen)}
        >
          <span className="truncate max-w-[460px]">{selectedOption || placeholder}</span>
          <DropdownFramer isDropdownVisible={isOpen}>
            <Image src={"dropdown-down-arrow"} />
          </DropdownFramer>
        </div>

        {/* Flat search panel */}
        {isOpen && (
          <motion.div
            className={`${dropdownTopClass || "top-[57px]"} absolute bg-white z-50 rounded-lg border border-P_light_active p-3.5`}
            style={{ width: buttonWidth }}
          >
            {/* Static options (e.g. ZoodPay) */}
            {staticOptions.length > 0 && (
              <div className="mb-2">
                {staticOptions.map((option, index) => (
                  <div
                    key={index}
                    className="flex items-center p-2 text-primary_text text-subTitle hover:bg-information_light hover:rounded-lg cursor-pointer"
                    onClick={() => {
                      onSelect && onSelect(option.value, false);
                      setIsOpen(false);
                      setInternalSearchTerm("");
                      setUsers([]);
                    }}
                    data-testid={option.testId || option.id || option.value.toLowerCase()}
                  >
                    <span>{option.label}</span>
                  </div>
                ))}
              </div>
            )}

            {/* Search input */}
            <div className="relative mb-3">
              <div className="absolute inset-y-0 left-0 flex items-center pl-3 pointer-events-none">
                <Image src={"active_search"} className="w-4 h-4" />
              </div>
              <input
                type="text"
                className="w-full px-4 py-2 pl-10 text-sm border border-P_light_active rounded-md focus:outline-hidden focus:border-p_primary"
                placeholder="Search"
                value={internalSearchTerm}
                onChange={(e) => handleSearch(e.target.value)}
                autoFocus
              />
            </div>

            {/* User list */}
            <div
              className={`overflow-y-auto ${getFirefoxCssFix("scroll")} scroll-auto max-h-60`}
              onScroll={handleScroll}
            >
              {loading && users.length === 0 ? (
                <div className="space-y-3 animate-pulse">
                  {[...Array(5)].map((_, i) => (
                    <div key={i} className="flex items-center space-x-2">
                      <div className="h-5 bg-gray-200 rounded-sm w-32" />
                      <div className="h-5 bg-gray-200 rounded-sm w-20" />
                      <div className="h-5 bg-gray-200 rounded-sm w-16" />
                    </div>
                  ))}
                </div>
              ) : users.length === 0 ? (
                <p className="py-2 text-sm text-gray-500">No data found.</p>
              ) : (
                users.map((user, index) => (
                  <div
                    key={index}
                    className="flex items-center justify-between p-2 mb-1 mr-1 text-sm cursor-pointer hover:bg-information_light hover:rounded-lg"
                    onClick={() => handleSelect(user)}
                    data-testid={`${user.user_name.toLowerCase().split(" ").join("-")}`}
                  >
                    {showStatus ? (
                      <div className="flex flex-row items-center gap-2 w-full">
                        <span className="font-medium break-all w-1/3">{user.user_name}</span>
                        <span className="text-xs text-gray-500 w-1/3">{user.user_code}</span>
                        <span className="px-2 py-1 text-center text-p_primary text-xs h-fit rounded-full w-1/3 bg-p_light">
                          {user.status}
                        </span>
                      </div>
                    ) : (
                      <div className="flex flex-row items-center gap-2 w-full">
                        <span className="font-medium break-all w-[62.5%]">{user.user_name}</span>
                        <span className="text-xs text-gray-500 w-[37.5%]">{user.user_code}</span>
                      </div>
                    )}
                  </div>
                ))
              )}
              {loading && users.length > 0 && (
                <div className="flex justify-center py-2">
                  <div className="w-5 h-5 border-2 border-gray-300 rounded-full animate-spin border-t-p_primary" />
                </div>
              )}
            </div>
          </motion.div>
        )}
      </div>

      {errorMessage && (
        <div className="mb-1 mt-1">
          <ErrorMessage
            errorMessage={errorMessage}
            testId={`error-${testId || id || "nested-dropdown"}`}
          />
        </div>
      )}
    </DropDownModel>
  );
};

export default NestedDropdown;
